// src/pages/Announcements.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface Announcement {
  id: number;
  title: string;
  summary: string;
  content: string;
  image: string | null;
  status: string;
  is_featured: number;
  views: number;
  created_at: string;
  author_name?: string;
}

interface BannerItem {
  id: string;
  title: string;
  description: string;
  link: string;
  image: string;
  bgColor: string;
  type: 'weather' | 'banner' | 'link';
}

const Announcements: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [featured, setFeatured] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [weatherData, setWeatherData] = useState<any>(null);

  // ── بنرهای پایین صفحه ──────────────────────────────────
  const banners: BannerItem[] = [
    {
      id: 'weather',
      title: '🌤️ وضعیت آب و هوا',
      description: 'آب و هوای امروز تهران',
      link: '#',
      image: '',
      bgColor: 'linear-gradient(135deg, #1a237e, #0d47a1)',
      type: 'weather'
    },
    {
      id: 'discount',
      title: '🔥 تخفیف ویژه',
      description: 'تا ۵۰٪ تخفیف برای محصولات ارگانیک',
      link: '/shop',
      image: '/images/banner-discount.jpg',
      bgColor: 'linear-gradient(135deg, #e65100, #f57c00)',
      type: 'banner'
    },
    {
      id: 'new-products',
      title: '🌱 محصولات جدید',
      description: 'بذر و کودهای ارگانیک با کیفیت بالا',
      link: '/shop?category=new',
      image: '/images/banner-products.jpg',
      bgColor: 'linear-gradient(135deg, #1b5e20, #2e7d32)',
      type: 'banner'
    },
    {
      id: 'event',
      title: '📅 رویداد پیش رو',
      description: 'نمایشگاه کشاورزی سبز - ۱۵ مهر ۱۴۰۴',
      link: '/events',
      image: '/images/banner-event.jpg',
      bgColor: 'linear-gradient(135deg, #4a148c, #6a1b9a)',
      type: 'banner'
    }
  ];

  // ── دریافت اطلاعیه‌ها ──────────────────────────────────
  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await fetch('https://fellfell.ir/api/announcements.php', {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        const items = data.data || [];
        const featuredItem = items.find((a: Announcement) => a.is_featured === 1);
        setFeatured(featuredItem || null);
        setAnnouncements(items.filter((a: Announcement) => a.is_featured !== 1));
      } else {
        setError('خطا در دریافت اطلاعیه‌ها');
      }
    } catch (err) {
      setError('خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  // ── دریافت داده‌های آب و هوا ────────────────────────────
  useEffect(() => {
    fetch('https://wttr.in/Tehran?format=j1')
      .then(res => res.json())
      .then(data => setWeatherData(data))
      .catch(() => console.log('خطا در دریافت آب و هوا'));
  }, []);

  // ── تابع نمایش وضعیت آب و هوا ──────────────────────────
  const renderWeatherWidget = () => {
    if (!weatherData) {
      return (
        <div style={{ textAlign: 'center', padding: '20px', color: '#fff' }}>
          <div style={{ 
            display: 'inline-block', 
            width: '30px', 
            height: '30px', 
            border: '3px solid #fff', 
            borderTopColor: 'transparent', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite' 
          }} />
          <p style={{ marginTop: '10px' }}>بارگذاری...</p>
        </div>
      );
    }

    const current = weatherData.current_condition[0];
    const temp = current.temp_C;
    const desc = current.weatherDesc[0].value;
    const icon = current.weatherIconUrl[0].value;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', color: '#fff', justifyContent: 'center' }}>
        <img src={icon} alt={desc} style={{ width: '64px', height: '64px', filter: 'brightness(0) invert(1)' }} />
        <div>
          <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{temp}°C</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>{desc}</div>
          <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
            احساس دما: {current.FeelsLikeC}°C | رطوبت: {current.humidity}%
          </div>
        </div>
      </div>
    );
  };

  if (loading) return <div className="loading">⏳ در حال بارگذاری...</div>;
  if (error) return <div className="alert alert-error">{error}</div>;

  return (
    <div style={{ direction: 'rtl', padding: '20px' }}>
      {/* ── بنر بالایی (ویژه) ────────────────────────────── */}
      {featured && (
        <div style={{
          background: 'linear-gradient(135deg, #1e3a1e, #2e7d32)',
          borderRadius: '16px',
          padding: '30px 40px',
          marginBottom: '30px',
          color: '#fff',
          boxShadow: '0 4px 20px rgba(46,125,50,0.3)',
          cursor: 'pointer',
          transition: 'transform 0.2s',
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.01)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        onClick={() => window.location.href = `/announcement/${featured.id}`}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <span style={{ background: '#f5a623', color: '#1e3a1e', padding: '4px 16px', borderRadius: '30px', fontSize: '14px', fontWeight: 'bold' }}>
                ⭐ ویژه
              </span>
              <h2 style={{ margin: '12px 0 8px', fontSize: '28px' }}>{featured.title}</h2>
              <p style={{ opacity: 0.9, fontSize: '16px' }}>{featured.summary || featured.content.substring(0, 150) + '...'}</p>
              <span style={{ fontSize: '13px', opacity: 0.7, marginTop: '8px', display: 'inline-block' }}>
                {new Date(featured.created_at).toLocaleDateString('fa-IR')} - {featured.views} بازدید
              </span>
            </div>
            {featured.image && (
              <img
                src={`/uploads/announcements/${featured.image}`}
                alt={featured.title}
                style={{ width: '180px', height: '120px', objectFit: 'cover', borderRadius: '12px', marginTop: '12px' }}
                onError={(e) => (e.currentTarget.style.display = 'none')}
              />
            )}
          </div>
        </div>
      )}

      {/* ── چهار بنر پایینی (دو سطر، هر سطر دو بنر) ────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
        {banners.map((banner) => (
          <div
            key={banner.id}
            style={{
              background: banner.bgColor,
              borderRadius: '16px',
              padding: '25px 30px',
              color: '#fff',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
              minHeight: '120px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'transform 0.2s',
              cursor: banner.type !== 'weather' ? 'pointer' : 'default'
            }}
            onMouseEnter={(e) => {
              if (banner.type !== 'weather') {
                e.currentTarget.style.transform = 'scale(1.02)';
              }
            }}
            onMouseLeave={(e) => {
              if (banner.type !== 'weather') {
                e.currentTarget.style.transform = 'scale(1)';
              }
            }}
            onClick={() => {
              if (banner.type !== 'weather' && banner.link !== '#') {
                window.location.href = banner.link;
              }
            }}
          >
            {banner.type === 'weather' ? (
              // ── ویجت آب و هوا ──────────────────────────────
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>{banner.title}</h3>
                {renderWeatherWidget()}
              </div>
            ) : (
              // ── بنرهای تبلیغاتی ────────────────────────────
              <>
                <div>
                  <h3 style={{ margin: 0, fontSize: '20px' }}>{banner.title}</h3>
                  <p style={{ opacity: 0.9, margin: '6px 0 0', fontSize: '14px' }}>{banner.description}</p>
                  <span style={{ display: 'inline-block', marginTop: '10px', background: 'rgba(255,255,255,0.2)', padding: '4px 16px', borderRadius: '20px', fontSize: '13px' }}>
                    مشاهده →
                  </span>
                </div>
                {banner.image && (
                  <img
                    src={banner.image}
                    alt={banner.title}
                    style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '12px' }}
                    onError={(e) => (e.currentTarget.style.display = 'none')}
                  />
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* ── لیست اطلاعیه‌ها به صورت کارت ────────────────── */}
      <h2 style={{ color: '#1e3a1e', marginBottom: '20px' }}>📢 آخرین اطلاعیه‌ها</h2>
      {announcements.length === 0 ? (
        <p style={{ color: '#888', textAlign: 'center' }}>هیچ اطلاعیه‌ای منتشر نشده است.</p>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '25px'
        }}>
          {announcements.map((item) => (
            <Link
              to={`/announcement/${item.id}`}
              key={item.id}
              style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '20px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                textDecoration: 'none',
                color: 'inherit',
                transition: 'transform 0.2s, box-shadow 0.2s',
                display: 'block',
                border: '1px solid #eef5ee'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
              }}
            >
              {item.image && (
                <img
                  src={`/uploads/announcements/${item.image}`}
                  alt={item.title}
                  style={{ width: '100%', height: '180px', objectFit: 'cover', borderRadius: '12px', marginBottom: '12px' }}
                  onError={(e) => (e.currentTarget.style.display = 'none')}
                />
              )}
              <h3 style={{ fontSize: '18px', color: '#1e3a1e', marginBottom: '8px' }}>{item.title}</h3>
              <p style={{ color: '#555', fontSize: '14px', lineHeight: '1.7' }}>
                {item.summary || item.content.substring(0, 120) + '...'}
              </p>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', fontSize: '13px', color: '#888' }}>
                <span>📅 {new Date(item.created_at).toLocaleDateString('fa-IR')}</span>
                <span>👁 {item.views}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Announcements;